const HEADER_ROW_BUILDERS = [
  report_title_row,
  report_category_row,
  report_date_range_row,
  report_scope_row,
  report_description_row,
  blank_row,
];

export function structured_report_to_csv(report = {}, options = {}) {
  return structured_report_sheet_rows(report, options)
    .map((row) => row.cells.map(csv_value).join(','))
    .join('\n');
}

export function structured_report_sheet_rows(report = {}, options = {}) {
  const rows = report_header_rows(report, options);
  report.sections.forEach((section) => {
    rows.push(...section_rows(section));
  });
  return rows;
}

function report_header_rows(report = {}, options = {}) {
  const context = { report, options };
  return HEADER_ROW_BUILDERS.map((builder) => builder(context));
}

function report_title_row({ report = {} } = {}) {
  return styled_row([with_fallback(report.title, 'Company report')], 'title');
}

function report_category_row({ report = {} } = {}) {
  return styled_row(['Category', with_fallback(report.category_label)]);
}

function report_date_range_row({ options = {} } = {}) {
  return styled_row(['Date Range', with_fallback(options.date_range)]);
}

function report_scope_row({ options = {} } = {}) {
  return styled_row(['Scope', with_fallback(options.scope)]);
}

function report_description_row({ report = {} } = {}) {
  return styled_row(['Description', with_fallback(report.description)]);
}

function blank_row() {
  return styled_row([]);
}

function with_fallback(value, fallback = '') {
  return value || fallback;
}

function section_rows(section = {}) {
  const build_rows = section_row_builder(section.type);
  return [
    styled_row([section.title || 'Report section'], 'section'),
    styled_row([section.description || '']),
    ...build_rows(section),
    styled_row([]),
  ];
}

function section_row_builder(type = '') {
  return section_builders().get(type) || table_section_rows;
}

function section_builders() {
  return new Map([
    ['metrics', metric_section_rows],
    ['grouped_entries', grouped_entry_section_rows],
  ]);
}

function metric_section_rows(section = {}) {
  return (section.rows || []).map(metric_row);
}

function metric_row(row = {}) {
  return styled_row([row.metric || '', row.value ?? '']);
}

function table_section_rows(section = {}) {
  const columns = section.columns || columns_from_rows(section.rows);
  return [
    styled_row(columns.map((column) => column.label), 'header'),
    ...table_body_rows(section.rows, columns),
  ];
}

function table_body_rows(rows = [], columns = []) {
  return (rows || []).map((row) => table_body_row(row, columns));
}

function table_body_row(row = {}, columns = []) {
  return styled_row(columns.map((column) => column_value(row, column)));
}

function grouped_entry_section_rows(section = {}) {
  return section_groups(section).flatMap(grouped_entry_group_rows);
}

function grouped_entry_group_rows(group = {}) {
  return [
    styled_row([group.title || 'Matter'], 'subsection'),
    styled_row(['Date', 'Timekeeper', 'Hours', 'Total Value', 'Task'], 'header'),
    ...group_rows(group).map(grouped_entry_row),
    styled_row([]),
  ];
}

function section_groups(section = {}) {
  return section.groups || [];
}

function group_rows(group = {}) {
  return group.rows || [];
}

function grouped_entry_row(row = {}) {
  return styled_row(grouped_entry_cells(row));
}

function grouped_entry_cells(row = {}) {
  return [
    empty_when_falsey(row.date),
    empty_when_falsey(row.timekeeper),
    hours_label(row),
    empty_when_falsey(row.total_value),
    empty_when_falsey(row.task),
  ];
}

function empty_when_falsey(value) {
  return value || '';
}

function columns_from_rows(rows = []) {
  return [...new Set(rows.flatMap(row_keys))]
    .map(column_from_key);
}

function row_keys(row = {}) {
  return Object.keys(row);
}

function column_from_key(key) {
  return {
    key,
    label: title_label(key),
  };
}

function hours_label(row = {}) {
  const hours = Number(row.billable_hours || row.non_billable_hours || 0);
  return hours ? `${hours} hrs` : '';
}

function styled_row(cells = [], style = '') {
  return { cells, style };
}

function csv_value(value = '') {
  const normalized = String(value ?? '').replace(/"/g, '""');
  return `"${normalized}"`;
}

function title_label(value = '') {
  return String(value || '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (character) => character.toUpperCase());
}

function safe_value(row = {}, key = '') {
  const entry = Object.entries(row).find(([entry_key]) => entry_key === key);
  return entry_value(entry);
}

function entry_value(entry) {
  return Array.isArray(entry) && entry.length > 1 ? entry[1] ?? '' : '';
}

function column_value(row = {}, column = {}) {
  return safe_value(row, column.key);
}
